import React, { useEffect, useState } from "react";
import { TableCell, TableHead, TableRow, TableContainer, Table, TableBody, Toolbar, Typography } from "@mui/material";
import { Done, Edit, Undo } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AutomaticApprovalSettings, UserRoles } from "../../../Constant";
import AutomaticApprovalApiService from "../../../adapters/xhr/AutomaticApprovalApiService";
import authService from "../../../components/api-authorization/AuthorizeService";
import { MyNumberField } from "../../../components/my-number-field/MyNumberField";
import SnackbarUtils from "../../../SnackbarUtils";
import { isValidAutomaticApprovalValue, mapAutomaticApprovalRows } from "./AutomaticApprovalSettingsHelper";

export const FraudNotificationSection = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [previousRows, setPreviousRows] = useState([]);
  const isReadOnly = authService._user?.role == UserRoles.CaseManager;
  const hasEditRow = rows.some((item) => item.isEditMode);

  const columns = [
    { id: "Title", label: t("title"), minWidth: 400, width: 400, editable: false },
    { id: "Value", label: t("value"), minWidth: 400, width: 400, editable: true },
    { id: "Actions", label: t("actions"), minWidth: 170, hidden: isReadOnly },
  ];

  useEffect(() => {
    fetchSettingsAsync();
  }, []);

  const fetchSettingsAsync = async () => {
    const response = await AutomaticApprovalApiService.getAll();
    if (response?.isSuccess) {
      const data = mapAutomaticApprovalRows(response.data, t);
      setRows(data);
      setPreviousRows(data);
    }
  };

  const filteredRows = rows
    ? [
        rows.find((item) => item.Key === AutomaticApprovalSettings.MaxClaimsIn24Hours),
        rows.find((item) => item.Key === AutomaticApprovalSettings.MaxClaimsDuring7Days),
        rows.find((item) => item.Key === AutomaticApprovalSettings.MaxClaimsDuring30Days),
      ].filter(Boolean)
    : [];

  const onEditRow = (id) => {
    setRows(
      previousRows.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: true };
        }

        return row;
      })
    );
  };

  const onChangeRowValues = (value, name, row) => {
    if (name === "Value" && !isValidAutomaticApprovalValue(row, value)) {
      return;
    }

    setRows(
      rows.map((item) => {
        if (item.id === row.id) {
          return { ...item, [name]: value };
        }

        return item;
      })
    );
  };

  const onRevertChanges = () => {
    setRows(previousRows);
  };

  const onSaveSettingsAsync = async (id) => {
    const row = rows.find((item) => item.id === id);
    
    if (!row.Value) return;

    const payload = { key: row.Key, value: `${row.Value}` };
    const response = await AutomaticApprovalApiService.update(row.Key, payload);
    
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage ?? "error"));
      onRevertChanges();
      return;
    }

    await fetchSettingsAsync();
  };

  return (
    <>
      <Toolbar>
        <Typography variant="h6" id="tableTitle" component="div">
          {t("fraud_notification")}
        </Typography>
      </Toolbar>
      <TableContainer sx={{ maxHeight: 440 }} className="margin bottom-7">
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {columns
                .filter((item) => !item.hidden)
                .map((column, key) => (
                  <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                    {column.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row, key) => {
              return (
                <TableRow hover tabIndex={-1} key={key}>
                  {columns
                    .filter((item) => !item.hidden)
                    .map((column) => {
                      if (column.id === "Actions") {
                        return (
                          <TableCell key={100} align="left">
                            {!row.isEditMode && !hasEditRow && <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onEditRow(row.id)} />}
                            {row.isEditMode && (
                              <>
                                <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveSettingsAsync(row.id)} />
                                <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertChanges()} />
                              </>
                            )}
                          </TableCell>
                        );
                      }

                      let value = row[column.id];

                      if ((row.isEditMode || row.isAddMode) && column.editable) {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <MyNumberField size="small" value={value} onChange={(e) => onChangeRowValues(e.target.value, column.id, row)} />
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={column.id} align={column.align}>
                            {value}
                        </TableCell>
                      );
                    })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};    